var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { DEFAULT_LEAD_TIME } from './OrderConst';
import Validator from '../libraries/Validator';
import ProductSelection, { extractProductsFromProductSelection } from './ProductSelection';
import AppError from '../libraries/AppError';
import check from '../libraries/check-types-ext';
import { concat, flatten, flattenDeep, intersection, isArray, isEmpty, uniq } from 'lodash';
import TenantSpecificOrderValidation from './TenantSpecificOrderValidation';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import getLuxonImpl from '../plugins/commonLibraries/timezone/timeboxImpl';
import { ApmHelper, ApmSpanType } from '../libraries/ApmHelper';
import { ReceiptLineType } from './ReceiptLine';
import { formatPrice } from '../libraries/Utils';
import { mapToReceiptLineViewG5 } from './ReceiptLineViewMapping';
import { mapToReceiptLinesG5 } from './ReceiptLineMapping';
import { makeOrderLinesBackwardsCompatibleG5 } from './OrderHelpers';
import OrderLine2, { OrderLineType } from './OrderLine2';
import { PriceMgr } from './PriceMgr';
import { DateTime } from 'luxon';
import { SUNSET_BM_ID, CATERING_BM_ID } from '../tenants/BusinessModelMgr';
import { getDates } from '../helpers/dateHelpers';
import { generatePickupRulesFromShopOpeningHours, isClosedAccordingToOpeningHours } from '../libraries/Shops';
import { getTotalGlobalDiscount, getTotalPrice, getTotalPriceCents, getTotalPriceWithDiscount, getTotalPriceWithoutDiscount, } from './PriceCalculation';
const formatTime = (v) => `0${v}`.substr(-2, 2);
function indent(n) {
    const SPACES = '                                             ';
    return SPACES.substring(0, n);
}
const extractBoxDateParts = (boxDate) => ({
    day: boxDate.day().exec(),
    month: boxDate.month().exec(),
    year: boxDate.year().exec(),
    weekDay: boxDate.weekday().exec(),
});
export const getTimeObject = (timeString) => {
    const timeArray = timeString.split(':');
    return {
        hour: parseInt(timeArray[0]),
        minute: parseInt(timeArray[1]),
    };
};
export const timeGreater = (pickupTime1, pickupTime2) => {
    if (pickupTime1.hour > pickupTime2.hour) {
        return true;
    }
    if (pickupTime1.hour === pickupTime2.hour && pickupTime1.minute > pickupTime2.minute) {
        return true;
    }
    return false;
};
export const timeSmaller = (pickupTime1, pickupTime2) => {
    if (pickupTime1.hour < pickupTime2.hour) {
        return true;
    }
    if (pickupTime1.hour === pickupTime2.hour && pickupTime1.minute < pickupTime2.minute) {
        return true;
    }
    return false;
};
const dateAppliesToRule = ({ day, month, year, weekDay }, rule) => {
    const weekDayFilterApplies = (() => {
        if (rule.weekDayFilter) {
            const dateWeekDay = weekDay === 0 ? 7 : weekDay;
            return rule.weekDayFilter
                .split(',')
                .map((v) => parseInt(v))
                .includes(dateWeekDay);
        }
        else {
            return false;
        }
    })();
    const dateFilterApplies = (() => {
        const datesFiltersArray = isArray(rule.dateFilter) ? rule.dateFilter : [rule.dateFilter];
        return datesFiltersArray.some((dateFilter) => {
            if (dateFilter) {
                const [yearPattern, monthPattern, dayPattern] = dateFilter.split('-').map((v) => v.split(','));
                const check = (value, valuePattern) => valuePattern.includes('*') || valuePattern.map((v) => parseInt(v)).includes(value);
                return check(year, yearPattern) && check(month, monthPattern) && check(day, dayPattern);
            }
            else {
                return false;
            }
        });
    })();
    return weekDayFilterApplies || dateFilterApplies;
};
export const joinTimeRanges = (ranges, isUnion = false) => {
    const times = [];
    const comparisonCheck = (comparisonResult) => (isUnion ? comparisonResult <= 0 : comparisonResult > 0);
    [...ranges]
        .sort((a, b) => a.pickupRangeFrom.localeCompare(b.pickupRangeFrom) || a.pickupRangeTo.localeCompare(b.pickupRangeTo))
        .forEach((time) => {
        const lastTime = times[times.length - 1];
        if (!lastTime || lastTime.pickupRangeTo < time.pickupRangeFrom) {
            times.push(Object.assign({}, time));
        }
        else {
            if (comparisonCheck(lastTime.pickupRangeTo.localeCompare(time.pickupRangeTo))) {
                lastTime.pickupRangeTo = time.pickupRangeTo;
            }
            if (comparisonCheck(time.pickupRangeFrom.localeCompare(lastTime.pickupRangeFrom))) {
                lastTime.pickupRangeFrom = time.pickupRangeFrom;
            }
        }
    });
    return times;
};
export const intersectAllRangesOfTwoRules = (rangesA, rangesB) => {
    const result = [];
    let ai = 0;
    let bi = 0;
    while (ai < rangesA.length && bi < rangesB.length) {
        const ax = rangesA[ai].pickupRangeFrom;
        const ay = rangesA[ai].pickupRangeTo;
        const bx = rangesB[bi].pickupRangeFrom;
        const by = rangesB[bi].pickupRangeTo;
        if (ay <= bx) {
            ai++;
        }
        else if (by <= ax) {
            bi++;
        }
        else {
            result.push({
                pickupRangeFrom: ax > bx ? ax : bx,
                pickupRangeTo: ay < by ? ay : by,
            });
            if (ay < by) {
                ai++;
            }
            else {
                bi++;
            }
        }
    }
    return result;
};
export const intersectAllRules = (rules) => {
    const rangesArrCp = rules.map((rule) => joinTimeRanges(rule.pickupRanges, true));
    return (rangesArrCp.reduce((acc, cur) => {
        if (!acc) {
            return acc;
        }
        else {
            return intersectAllRangesOfTwoRules(acc, cur);
        }
    }, rangesArrCp.shift()) || []);
};
const getAllApplicableRules = ({ shopData, convertedBoxDate, pickupDateTimeRules, tenantData, TimeBoxImpl, pickupTimeZone, useShopOpeningHoursByWeekday = false, }) => {
    const stringDate = convertedBoxDate.format(TimeBoxImpl.Formats.YYYY_MM_DD).exec();
    const dateParts = extractBoxDateParts(convertedBoxDate);
    const defaultRule = (() => {
        if (shopData !== null && Array.isArray(shopData.shopWorkingHours)) {
            const shopRule = shopData.shopWorkingHours.find((swh) => swh.date === stringDate && swh.type === 'store');
            if (shopRule) {
                if (shopRule.closed && shopRule.open === null && shopRule.close === null) {
                    return null;
                }
                else {
                    const openWorkingHour = shopRule.open.split('T')[1].split(':')[0];
                    const openWorkingMinute = shopRule.open.split('T')[1].split(':')[1];
                    const closeWorkingHour = shopRule.close.split('T')[1].split(':')[0];
                    const closeWorkingMinute = shopRule.close.split('T')[1].split(':')[1];
                    return {
                        tenantId: tenantData.tenantId,
                        dateFilter: '*-*-*',
                        weekDayFilter: null,
                        shopFilter: null,
                        productFilter: null,
                        pickupRanges: [
                            {
                                pickupRangeFrom: `${openWorkingHour}:${openWorkingMinute}:00`,
                                pickupRangeTo: `${closeWorkingHour}:${closeWorkingMinute}:00`,
                            },
                        ],
                    };
                }
            }
        }
        return {
            tenantId: tenantData.tenantId,
            dateFilter: '*-*-*',
            weekDayFilter: null,
            shopFilter: null,
            productFilter: null,
            pickupRanges: [
                {
                    pickupRangeFrom: `${formatTime(tenantData.defaultPickupStartTime.hour)}:${formatTime(tenantData.defaultPickupStartTime.minute)}:00`,
                    pickupRangeTo: +tenantData.defaultPickupEndTime.hour > 23
                        ? '23:59:00'
                        : `${formatTime(tenantData.defaultPickupEndTime.hour)}:${formatTime(tenantData.defaultPickupEndTime.minute)}:00`,
                },
            ],
        };
    })();
    const shopId = shopData ? shopData.id : null;
    const filteredByProductDepartmentShopRules = OrderHelperObject.getFilteredRules({
        rules: pickupDateTimeRules,
        productIds: [],
        departmentIds: [],
        shopId,
        useShopOpeningHoursByWeekday,
    });
    const unavailableDates = OrderHelperObject.getUnavailableDates({
        pickupDateTimeRules: [
            ...filteredByProductDepartmentShopRules.filter((rule) => !rule.isWhitelist && !isEmpty(rule.dateFilter)),
        ],
        pickupTimeZone,
    });
    if (unavailableDates.length > 0 && unavailableDates.includes(stringDate)) {
        return [];
    }
    if (isClosedAccordingToOpeningHours({
        shopData,
        useShopOpeningHoursByWeekday,
        dateParts,
    })) {
        return [];
    }
    const whitelistedDates = OrderHelperObject.getUnavailableDates({
        pickupDateTimeRules: filteredByProductDepartmentShopRules.filter((rule) => rule.isWhitelist && isEmpty(rule.pickupRanges)),
        pickupTimeZone,
    });
    if (whitelistedDates.length > 0 && !whitelistedDates.includes(stringDate)) {
        return [];
    }
    const filteredRules = [
        ...filteredByProductDepartmentShopRules.filter((rule) => {
            return !isEmpty(rule.pickupRanges) && dateAppliesToRule(dateParts, rule);
        }),
    ];
    const applicableRules = (() => {
        if (filteredRules.length === 0 && defaultRule === null)
            return [];
        if (filteredRules.length === 0)
            return [defaultRule];
        return filteredRules;
    })().concat(generatePickupRulesFromShopOpeningHours({
        dateParts,
        shopData,
        useShopOpeningHoursByWeekday,
    }));
    return intersectAllRules(applicableRules).map(({ pickupRangeFrom: from, pickupRangeTo: to }) => ({ from, to }));
};
const OrderHelperObject = {
    validateOrderBasicDataOrFail({ order, isAdmin }) {
        const TimeBoxImpl = getLuxonImpl();
        if (!order.shopId && !isAdmin) {
            throw AppError.OrderShopUndefinedError();
        }
        if (!order.pickupDate) {
            throw AppError.OrderPickupDateUndefinedError();
        }
        if (!order.pickupTime) {
            throw AppError.OrderPickupTimeUndefinedError();
        }
        if (order.productSelections.length === 0) {
            throw AppError.OrderEmptyError();
        }
        try {
            const isValidDate = TimeBoxImpl.timebox
                .fromDateTimeString(order.pickupDate, order.pickupTime, order.pickupTimeZone)
                .valid()
                .exec();
            if (!isValidDate) {
                throw AppError.OrderPickupTimeInvalidError();
            }
        }
        catch (e) {
            throw AppError.OrderPickupTimeInvalidError(`${order.pickupDate} ${order.pickupTime} ${order.pickupTimeZone}`);
        }
    },
    validateOrderOrFail: ApmHelper.apmDecorator(({ order, tenantData, shopData, pickupDateTimeRules, assortmentRules, productCatalog, isEditMode = false, isAdmin = false, logger = undefined, useShopOpeningHoursByWeekday = false, }) => {
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        const TimeBoxImpl = getLuxonImpl();
        const realNowTimestamp = TimeBoxImpl.timebox
            .realNow()
            .convertTimezones(shopData.shopTimeZone, { keepLocalTime: true })
            .unix()
            .exec();
        const logEvent = (message, data) => {
            if (typeof logger === 'object' && typeof logger.logEvent === 'function') {
                logger.logEvent(message, 'OrderValidation', 'OrderValidation', 'OrdersDomain', data);
            }
        };
        OrderHelperObject.validateOrderBasicDataOrFail({ order, isAdmin });
        const hasExpired = order.productSelections.find((productSelection) => {
            if (productSelection.expiration &&
                productSelection.expiration.expirationTime &&
                TimeBoxImpl.timebox.fromMillis(productSelection.expiration.expirationTime).unix().exec() <
                    realNowTimestamp) {
                return true;
            }
            return false;
        });
        if (hasExpired) {
            throw AppError.createNewError('ProductSelectionExpiredError');
        }
        if (!isAdmin) {
            const hasInactive = order.productSelections.find((productSelected) => {
                const product = productCatalog.getProduct(productSelected.productId, true);
                return !product.isActive;
            });
            if (hasInactive) {
                throw AppError.ProductIsInactive();
            }
        }
        const hasNonRootProduct = order.productSelections.find((productSelected) => {
            const product = productCatalog.getProduct(productSelected.productId, true);
            return !product.canBeInRootOfProductSelection;
        });
        if (hasNonRootProduct) {
            throw AppError.ProductCannotBeRootProductSelection();
        }
        if (!order.customerDetails) {
            throw AppError.OrderCustomerDetailsUndefinedError();
        }
        if (shopData.shopOptions.paymentOptions && shopData.shopOptions.paymentOptions.shopPaymentOptionBypass) {
            if (order.paymentStatus === 'prepaid' && !shopData.shopOptions.paymentOptions.prepaidOrdersEnabled) {
                throw AppError.PrepaidPaymentsDisabledForShop();
            }
            if (order.paymentStatus === 'postpaid' && !shopData.shopOptions.paymentOptions.postpaidOrdersEnabled) {
                throw AppError.PostpaidPaymentsDisabledForShop();
            }
        }
        const productIds = OrderHelperObject.getProductIds({
            productSelections: order.productSelections,
            productCatalog,
            includeDisabled: isAdmin,
        }).filter((v, i, a) => a.indexOf(v) === i);
        const productsWithTwoForOne = productIds
            .map((id) => {
            return productCatalog.getProduct(id, isAdmin);
        })
            .filter((product) => {
            if (product.discount) {
                return product.discount.discountType === 'twoForOne';
            }
            return false;
        })
            .map((product) => {
            const totalQuantity = order.productSelections.reduce((acc, curr) => {
                if (curr.productId === product.id) {
                    return acc + curr.quantity;
                }
                return acc;
            }, 0);
            return totalQuantity % 2;
        })
            .reduce((acc, curr) => {
            return acc + curr;
        }, 0);
        if (productsWithTwoForOne > 0) {
            throw AppError.TwoForOneOddProductNumberError();
        }
        if (order.customerDetails && order.customerDetails.employeeDiscountNumber) {
            const employeeDiscountNumberRule = {
                employeeDiscountNumber: ['employeeDiscountNumber'],
            };
            const employeeDiscountNumberValidation = Validator.validateInputs({
                employeeDiscountNumber: order.customerDetails.employeeDiscountNumber,
            }, employeeDiscountNumberRule);
            if (Array.isArray(employeeDiscountNumberValidation['employeeDiscountNumber']) &&
                employeeDiscountNumberValidation['employeeDiscountNumber'].length > 0) {
                throw AppError.OrderEmployeeDiscountNumberInvalidError();
            }
        }
        if (order.customerDetails && order.customerDetails.employeeDiscountNumber === '') {
            delete order.customerDetails.employeeDiscountNumber;
        }
        const pickupDate = TimeBoxImpl.timebox.fromDateTimeString(order.pickupDate, order.pickupTime, shopData.shopTimeZone);
        if (!isEditMode && !isAdmin) {
            const isBM100 = tenantData.businessModelId === SUNSET_BM_ID;
            const assortmentRulesValidation = OrderHelperObject.assortmentRuleValidation({
                assortmentRules,
                productSelections: order.productSelections,
                shopId: shopData.id,
                tenantId: tenantData.tenantId,
                shouldAddProductIdsInsideGroups: isBM100,
            });
            if (assortmentRulesValidation !== true) {
                throw AppError.OrderAssortmentRuleBrokenError().addData('brokenRule', assortmentRulesValidation);
            }
            const useBTGOption = tenantData.businessModelId === CATERING_BM_ID && order.orderOptions.deliveryChoice === 2;
            const isNotValidProductSelections = OrderHelperObject.getProductIdsFromProductSelections(order.productSelections, isBM100).some((productId) => {
                return !OrderHelperObject.canBePickedUp({
                    boxDate: pickupDate,
                    productSelections: [ProductSelection.createFromProduct(productCatalog.getProduct(productId, true))],
                    tenantData: tenantData,
                    shopId: shopData.id,
                    pickupDateTimeRules,
                    productCatalog,
                    extraRules: {
                        useBTGOption,
                    },
                });
            });
            if (isNotValidProductSelections) {
                if (isBM100) {
                    throw AppError.OrderPickupDateInvalidErrorForBM100().addData('requested pickup date', order.pickupDate);
                }
                else {
                    throw AppError.OrderPickupDateInvalidError().addData('requested pickup date', order.pickupDate);
                }
            }
            const pickupDateTime = TimeBoxImpl.timebox.fromDateTimeString(order.pickupDate, order.pickupTime, order.pickupTimeZone);
            const canBePickedUpAtHours = OrderHelperObject.canBePickedUpAtTime({
                boxDate: pickupDateTime,
                productSelections: order.productSelections,
                tenantData,
                shopData,
                pickupDateTimeRules,
                productCatalog,
                useShopOpeningHoursByWeekday,
            });
            if (!canBePickedUpAtHours) {
                throw AppError.OrderPickupTimeInvalidError().addData('requested pickup date/time', `${order.pickupDate} ${order.pickupTime}`);
            }
            if (shopData.offersHeatableFood !== true && order.orderOptions.mustBeHeated) {
                throw AppError.ProductCannotBeHeatedError();
            }
            const foundError = order.productSelections.reduce((carry, productSelection) => {
                if (carry === false) {
                    return ProductSelection.validate(productCatalog.getProduct(productSelection.productId, true), productSelection, order.productSelections);
                }
                return carry;
            }, false);
            if (foundError) {
                logEvent('OrderInvalidProductSelection', order.productSelections);
                throw AppError.OrderInvalidProductSelection().addData('productSelection', order.productSelections);
            }
        }
        const data = {
            order,
            tenantData,
            shopData,
            pickupDateTimeRules,
            assortmentRules,
            isEditMode,
            isAdmin,
        };
        const tenantSpecificValidations = TenantSpecificOrderValidation(data)[`tid${tenantData.tenantId}`];
        if (Array.isArray(tenantSpecificValidations)) {
            tenantSpecificValidations.forEach((validationHandler) => {
                validationHandler();
            });
        }
    }, {
        apmLabel: 'OrderHelperValidateOrderOrFail',
        apmType: ApmSpanType.PURE_FUNCTION,
    }),
    hasHeatableProduct(productSelections, productCatalog) {
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        return ProductSelection.hasHeatableProduct(productCatalog, productSelections);
    },
    maxLeadDay({ productSelections, tenantData, includeDisabled = false, shopId = undefined, productCatalog }) {
        check.array.orFail(productSelections, 'productSelections');
        check.object.orFail(tenantData, 'tenantData');
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        const TimeBoxImpl = getLuxonImpl();
        const nowHour = TimeBoxImpl.timebox
            .now()
            .convertTimezones(productCatalog.pickupTimeZone)
            .format(TimeBoxImpl.Formats.HH_mm_ss)
            .exec();
        return productSelections.reduce((carry, product) => {
            const productId = typeof product.productId !== 'undefined' ? product.productId : product.id;
            const catalogProduct = productCatalog.getProduct(productId, includeDisabled);
            const isNumber = (number) => {
                return typeof number === 'number' && !isNaN(number);
            };
            const productLeadTime = (() => {
                if (typeof catalogProduct.options.leadTime !== 'undefined' && catalogProduct.options.leadTime !== null) {
                    const leadTimesShopSpecific = catalogProduct.options.leadTimesShopSpecific;
                    const emptyValues = [null, undefined, '', 0];
                    const leadTime = parseInt(catalogProduct.options.leadTime);
                    const leadTimeThreshold = catalogProduct.options.leadTimeThreshold;
                    if (Array.isArray(leadTimesShopSpecific) &&
                        leadTimesShopSpecific.length > 0 &&
                        !emptyValues.includes(shopId) &&
                        catalogProduct.options.leadTimesShopSpecificEnabled) {
                        const shopSpecificLeadTime = leadTimesShopSpecific.find((o) => o.shopId === shopId);
                        if (shopSpecificLeadTime && isNumber(shopSpecificLeadTime.leadTime)) {
                            const parsedLeadTime = parseInt(shopSpecificLeadTime.leadTime);
                            if (shopSpecificLeadTime.leadTimeThreshold && nowHour >= leadTimeThreshold) {
                                return parsedLeadTime + 1;
                            }
                            return parsedLeadTime;
                        }
                    }
                    if (isNumber(leadTime) && leadTimeThreshold) {
                        if (nowHour >= leadTimeThreshold) {
                            return leadTime + 1;
                        }
                    }
                    return leadTime;
                }
                if (typeof tenantData.defaultLeadTime !== 'undefined') {
                    return tenantData.defaultLeadTime;
                }
                return DEFAULT_LEAD_TIME;
            })();
            if (!isNaN(productLeadTime) && productLeadTime > carry) {
                return productLeadTime;
            }
            else {
                return carry;
            }
        }, 0);
    },
    allowedWeekDays: ({ pickupDateTimeRules }) => {
        check.array.orFail(pickupDateTimeRules, 'pickupDateTimeRules');
        return [1, 2, 3, 4, 5, 6, 7].filter((weekDay) => {
            if (pickupDateTimeRules.length === 0) {
                return true;
            }
            return pickupDateTimeRules.some((rule) => {
                const isExists = rule.weekDayFilter
                    .split(',')
                    .map((weekDay) => parseInt(weekDay))
                    .includes(weekDay);
                return rule.isWhitelist ? isExists : !isExists;
            });
        });
    },
    canBePickedUpAtTime: ApmHelper.apmDecorator(({ boxDate, productSelections, tenantData, shopData = null, pickupDateTimeRules, includeDisabled = false, includePastTimes = true, ignoreOffset = false, productCatalog, useShopOpeningHoursByWeekday = false, }) => {
        check.object.orFail(tenantData, 'tenantData');
        check.array.orFail(productSelections, 'productSelections');
        check.array.orFail(pickupDateTimeRules, 'pickupDateTimeRules');
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        const timezoneUsed = shopData ? shopData.shopTimeZone : productCatalog.pickupTimeZone;
        const TimeBoxImpl = getLuxonImpl();
        const convertedBoxDate = boxDate.convertTimezones(timezoneUsed);
        const dateParts = extractBoxDateParts(convertedBoxDate);
        if (shopData !== null) {
            check.object.orFail(shopData, 'shopData');
            if (shopData.shopWorkingHours !== null) {
                check.array.orFail(shopData.shopWorkingHours, 'shopWorkingHours');
            }
        }
        if (includePastTimes !== false) {
            const nowDateTime = TimeBoxImpl.timebox.now().convertTimezones(timezoneUsed);
            if (convertedBoxDate.lt(nowDateTime).exec()) {
                return false;
            }
        }
        const productIds = OrderHelperObject.getProductIds({
            productSelections,
            productCatalog,
            includeDisabled,
        });
        const departmentIds = OrderHelperObject.getDepartmentIds({
            productSelections,
            productCatalog,
            includeDisabled,
        });
        const stringDate = convertedBoxDate.format(TimeBoxImpl.Formats.YYYY_MM_DD).exec();
        const defaultRule = (() => {
            if (shopData !== null && Array.isArray(shopData.shopWorkingHours)) {
                const shopRule = shopData.shopWorkingHours.find((swh) => swh.date === stringDate && swh.type === 'store');
                if (shopRule) {
                    if (shopRule.closed && shopRule.open === null && shopRule.close === null) {
                        return null;
                    }
                    else {
                        const openWorkingHour = shopRule.open.split('T')[1].split(':')[0];
                        const openWorkingMinute = shopRule.open.split('T')[1].split(':')[1];
                        const closeWorkingHour = shopRule.close.split('T')[1].split(':')[0];
                        const closeWorkingMinute = shopRule.close.split('T')[1].split(':')[1];
                        return {
                            tenantId: tenantData.tenantId,
                            dateFilter: '*-*-*',
                            weekDayFilter: null,
                            shopFilter: null,
                            productFilter: null,
                            pickupRanges: [
                                {
                                    pickupRangeFrom: `${openWorkingHour}:${openWorkingMinute}:00`,
                                    pickupRangeTo: `${closeWorkingHour}:${closeWorkingMinute}:00`,
                                },
                            ],
                        };
                    }
                }
            }
            return {
                tenantId: tenantData.tenantId,
                dateFilter: '*-*-*',
                weekDayFilter: null,
                shopFilter: null,
                productFilter: null,
                pickupRanges: [
                    {
                        pickupRangeFrom: `${formatTime(tenantData.defaultPickupStartTime.hour)}:${formatTime(tenantData.defaultPickupStartTime.minute)}:00`,
                        pickupRangeTo: `${formatTime(tenantData.defaultPickupEndTime.hour)}:${formatTime(tenantData.defaultPickupEndTime.minute)}:00`,
                    },
                ],
            };
        })();
        if (!useShopOpeningHoursByWeekday && defaultRule === null) {
            return false;
        }
        const shopId = shopData ? shopData.id : null;
        const filteredByProductDepartmentShopRules = OrderHelperObject.getFilteredRules({
            rules: pickupDateTimeRules,
            productIds,
            departmentIds,
            shopId,
            useShopOpeningHoursByWeekday,
        });
        const unavailableDates = OrderHelperObject.getUnavailableDates({
            pickupDateTimeRules: [
                ...filteredByProductDepartmentShopRules.filter((rule) => !rule.isWhitelist && !isEmpty(rule.dateFilter)),
            ],
            pickupTimeZone: productCatalog.pickupTimeZone,
        });
        if (unavailableDates.length > 0) {
            if (unavailableDates.includes(stringDate)) {
                return false;
            }
        }
        if (isClosedAccordingToOpeningHours({
            shopData: shopData,
            useShopOpeningHoursByWeekday,
            dateParts,
        })) {
            return false;
        }
        const isDateInWhitelist = productIds.every((productId) => {
            const filteredByProductRules = OrderHelperObject.getFilteredRules({
                rules: pickupDateTimeRules,
                productIds: [productId],
                departmentIds,
                shopId,
            });
            const whitelistedDates = OrderHelperObject.getUnavailableDates({
                pickupDateTimeRules: filteredByProductRules.filter((rule) => rule.isWhitelist && isEmpty(rule.pickupRanges)),
                pickupTimeZone: productCatalog.pickupTimeZone,
            });
            return !(whitelistedDates.length > 0 && !whitelistedDates.includes(stringDate));
        });
        if (!isDateInWhitelist) {
            return false;
        }
        const filteredRules = [
            ...filteredByProductDepartmentShopRules.filter((rule) => {
                return rule.isWhitelist && !isEmpty(rule.pickupRanges) && dateAppliesToRule(dateParts, rule);
            }),
        ]
            .concat(generatePickupRulesFromShopOpeningHours({
            dateParts,
            shopData: shopData,
            useShopOpeningHoursByWeekday,
        }));
        const hasProductThatNoRuleApplies = productIds.some((productId) => {
            const departmentId = productCatalog.getProduct(productId, true).departmentId;
            return (filteredRules.filter((rule) => {
                if (Array.isArray(rule.productFilter) && !rule.productFilter.includes(productId)) {
                    return false;
                }
                if (Array.isArray(rule.departmentFilter) && !rule.departmentFilter.includes(departmentId)) {
                    return false;
                }
                return true;
            }).length === 0);
        });
        const timeFromDate = getTimeObject(`${convertedBoxDate.hour().exec()}:${convertedBoxDate.minute().exec()}:${convertedBoxDate.second().exec()}`);
        return ![
            ...(hasProductThatNoRuleApplies || filteredRules.length === 0 ? [defaultRule] : []),
            ...filteredRules,
        ].some((rule) => {
            const getAdjustedRangeTo = (pickupRangeTo) => {
                if (pickupRangeTo !== null &&
                    typeof tenantData.offsetTimeMinutesRangeTo === 'number' &&
                    ignoreOffset !== true) {
                    return TimeBoxImpl.timebox
                        .fromFormat(pickupRangeTo, TimeBoxImpl.Formats.HH_mm_ss)
                        .subtract({ minutes: tenantData.offsetTimeMinutesRangeTo })
                        .format(TimeBoxImpl.Formats.HH_mm_ss)
                        .exec();
                }
                return pickupRangeTo;
            };
            return rule.pickupRanges.every((pickupRange) => {
                const range = {
                    from: pickupRange.pickupRangeFrom,
                    to: getAdjustedRangeTo(pickupRange.pickupRangeTo),
                };
                return ((range.from !== null ? timeSmaller(timeFromDate, getTimeObject(range.from)) : false) ||
                    (range.to !== null ? timeGreater(timeFromDate, getTimeObject(range.to)) : false));
            });
        });
    }, {
        apmLabel: 'OrderHelperCanBePickedUpAtTime',
        apmType: ApmSpanType.PURE_FUNCTION,
    }),
    canBePickedUp: ApmHelper.apmDecorator(({ boxDate, productSelections, tenantData, shopId = null, pickupDateTimeRules, includeDisabled = false, productCatalog, extraRules = { useBTGOption: false }, hiddenItems = [], }) => {
        check.object.orFail(tenantData, 'tenantData');
        check.array.orFail(productSelections, 'productSelections');
        check.array.orFail(pickupDateTimeRules, 'pickupDateTimeRules');
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        const TimeBoxImpl = getLuxonImpl();
        if (shopId !== null) {
            check.string.orFail(shopId, 'shopId');
        }
        const convertedBoxDate = boxDate.convertTimezones(productCatalog.pickupTimeZone);
        const now = TimeBoxImpl.timebox.now().convertTimezones(productCatalog.pickupTimeZone);
        const boxDateWithoutTime = convertedBoxDate.set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        const nowWithoutTime = now.set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        if (boxDateWithoutTime.lt(nowWithoutTime).exec() ||
            (tenantData.allowOrderPickupSameDay !== true && boxDateWithoutTime.lte(nowWithoutTime).exec())) {
            return false;
        }
        const productIds = OrderHelperObject.getProductIds({
            productSelections,
            productCatalog,
            includeDisabled,
        });
        if ((hiddenItems === null || hiddenItems === void 0 ? void 0 : hiddenItems.length) > 0) {
            for (let i = 0; i < productIds.length; i++) {
                if (hiddenItems.includes(productIds[i])) {
                    return false;
                }
            }
        }
        const departmentIds = OrderHelperObject.getDepartmentIds({
            productSelections,
            productCatalog,
            includeDisabled,
        });
        const filteredRules = OrderHelperObject.getFilteredRules({
            rules: pickupDateTimeRules,
            productIds,
            departmentIds,
            shopId,
        });
        const pickupDateTimeRulesWithWeekDayFilter = filteredRules.filter((rule) => (rule.weekDayFilter || '') !== '');
        const allowedWeekDays = OrderHelperObject.allowedWeekDays({
            pickupDateTimeRules: pickupDateTimeRulesWithWeekDayFilter,
        });
        const isoWeekDay = convertedBoxDate.weekday().exec();
        if (!allowedWeekDays.includes(isoWeekDay)) {
            return false;
        }
        const unavailableDates = OrderHelperObject.getUnavailableDates({
            pickupDateTimeRules: filteredRules.filter((rule) => !rule.isWhitelist && !isEmpty(rule.dateFilter)),
            pickupTimeZone: productCatalog.pickupTimeZone,
        });
        const stringDate = convertedBoxDate.format(TimeBoxImpl.Formats.YYYY_MM_DD).exec();
        if (unavailableDates.length > 0) {
            if (unavailableDates.includes(stringDate)) {
                return false;
            }
        }
        if (extraRules.useBTGOption) {
            const btgUnavailableDates = OrderHelperObject.getBTGUnavailableDates();
            if (btgUnavailableDates.length > 0) {
                if (btgUnavailableDates.includes(stringDate)) {
                    return false;
                }
            }
        }
        const whitelistedDates = OrderHelperObject.getUnavailableDates({
            pickupDateTimeRules: filteredRules.filter((rule) => rule.isWhitelist && isEmpty(rule.pickupRanges)),
            pickupTimeZone: productCatalog.pickupTimeZone,
            onlyIntersection: true,
        });
        if (whitelistedDates.length > 0) {
            if (!whitelistedDates.includes(stringDate)) {
                return false;
            }
        }
        const unavailableDatesWithoutProducts = OrderHelperObject.getUnavailableDates({
            pickupDateTimeRules: filteredRules.filter((rule) => !rule.isWhitelist && !isEmpty(rule.dateFilter) && !Array.isArray(rule.productFilter)),
            pickupTimeZone: productCatalog.pickupTimeZone,
        });
        const findLeadTimeUnavailableDates = (fromDate, maxLeadTime) => {
            if (maxLeadTime > 0) {
                const fromDateString = fromDate.format(TimeBoxImpl.Formats.YYYY_MM_DD).exec();
                if (unavailableDatesWithoutProducts.includes(fromDateString)) {
                    return findLeadTimeUnavailableDates(fromDate.add({ days: 1 }), maxLeadTime);
                }
                else {
                    return [fromDateString, ...findLeadTimeUnavailableDates(fromDate.add({ days: 1 }), maxLeadTime - 1)];
                }
            }
            else {
                return [];
            }
        };
        const maxLeadTime = OrderHelperObject.maxLeadDay({
            productSelections,
            tenantData,
            includeDisabled,
            shopId: shopId,
            productCatalog,
        });
        const leadTimeUnavailableDates = findLeadTimeUnavailableDates(TimeBoxImpl.timebox.now().add({ days: 1 }).convertTimezones(productCatalog.pickupTimeZone), maxLeadTime);
        if (leadTimeUnavailableDates.length > 0) {
            if (leadTimeUnavailableDates.includes(stringDate)) {
                return false;
            }
        }
        return true;
    }, {
        apmLabel: 'OrderHelperCanBePickedUp',
        apmType: ApmSpanType.PURE_FUNCTION,
    }),
    getEarliestPickupDate: ApmHelper.apmDecorator(({ boxFromDate, products, tenantData, pickupDateTimeRules, shopId = null, includeDisabled = false, productCatalog, }) => {
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        const TimeBoxImpl = getLuxonImpl();
        let convertedBoxFromDate = boxFromDate.convertTimezones(productCatalog.pickupTimeZone);
        const latestOrderDate = TimeBoxImpl.timebox
            .now()
            .convertTimezones(productCatalog.pickupTimeZone)
            .add({ years: 1 });
        if (boxFromDate.gt(latestOrderDate).exec()) {
            return null;
        }
        while (convertedBoxFromDate.lte(latestOrderDate).exec()) {
            if (OrderHelperObject.canBePickedUp({
                boxDate: convertedBoxFromDate,
                productSelections: products,
                tenantData,
                pickupDateTimeRules,
                includeDisabled,
                shopId,
                productCatalog,
            })) {
                return convertedBoxFromDate;
            }
            convertedBoxFromDate = convertedBoxFromDate.add({ days: 1 });
        }
        return null;
    }, {
        apmLabel: 'OrderHelperGetEarliestPickupDate',
        apmType: ApmSpanType.PURE_FUNCTION,
    }),
    getEarliestPickupTime: ApmHelper.apmDecorator(({ boxDate, tenantData, pickupDateTimeRules, shopData = null, includeDisabled = false, includePastTimes = true, ignoreOffset = false, pickupTimeZone, }) => {
        check.string.orFail(pickupTimeZone, 'pickupTimeZone');
        const TimeBoxImpl = getLuxonImpl();
        const convertedBoxDate = boxDate.convertTimezones(pickupTimeZone);
        const allTimes = getAllApplicableRules({
            shopData: shopData,
            pickupDateTimeRules,
            convertedBoxDate,
            TimeBoxImpl,
            tenantData,
            pickupTimeZone,
        });
        const timeNow = convertedBoxDate.format(TimeBoxImpl.Formats.HH_mm).exec();
        const inRange = allTimes.reverse().find((time) => time.from <= timeNow && time.to > timeNow);
        const timeFound = allTimes.map(({ from }) => from).find((fromTime) => fromTime > timeNow);
        const timeToUse = inRange ? inRange.from : timeFound;
        if (!timeToUse) {
            return null;
        }
        const [hour, minute] = timeToUse.split(':');
        const boxOpeningHours = convertedBoxDate.set({
            hour,
            minute,
            second: 0,
        });
        if (OrderHelperObject.canBePickedUpAtTime({
            boxDate: boxOpeningHours,
            productSelections: [],
            tenantData,
            shopData,
            pickupDateTimeRules,
            includeDisabled,
            includePastTimes,
            ignoreOffset,
            productCatalog: {
                pickupTimeZone,
            },
        })) {
            return boxOpeningHours.format(TimeBoxImpl.Formats.HH_mm).exec();
        }
        else {
            const openingHours = boxOpeningHours.format(TimeBoxImpl.Formats.HH_mm).exec();
            throw new Error(`${shopData.shopNameAlias} have selected wrong opening hours of ${openingHours}`);
        }
    }, {
        apmLabel: 'OrderHelperGetEarliestPickupTime',
        apmType: ApmSpanType.PURE_FUNCTION,
    }),
    getLatestPickupTime({ boxDate, tenantData, pickupDateTimeRules, shopData = null, includeDisabled = false, includePastTimes = true, ignoreOffset = false, pickupTimeZone, useShopOpeningHoursByWeekday = false, }) {
        check.string.orFail(pickupTimeZone, 'pickupTimeZone');
        const TimeBoxImpl = getLuxonImpl();
        const convertedBoxDate = boxDate.convertTimezones(pickupTimeZone);
        const allTimes = getAllApplicableRules({
            shopData: shopData,
            pickupDateTimeRules,
            convertedBoxDate,
            TimeBoxImpl,
            tenantData,
            pickupTimeZone,
            useShopOpeningHoursByWeekday,
        });
        const timeNow = convertedBoxDate.format(TimeBoxImpl.Formats.HH_mm).exec();
        const timeToUse = allTimes
            .filter((time) => time.to > timeNow)
            .map(({ to }) => to)
            .find(Boolean);
        if (!timeToUse) {
            return null;
        }
        const [hour, minute] = timeToUse.split(':');
        const boxClosingHours = convertedBoxDate.set({
            hour,
            minute,
            second: 0,
        });
        if (OrderHelperObject.canBePickedUpAtTime({
            boxDate: boxClosingHours,
            productSelections: [],
            tenantData,
            shopData,
            pickupDateTimeRules,
            includeDisabled,
            includePastTimes,
            ignoreOffset,
            productCatalog: {
                pickupTimeZone,
            },
            useShopOpeningHoursByWeekday,
        })) {
            return boxClosingHours.format(TimeBoxImpl.Formats.HH_mm).exec();
        }
        else {
            const closingHours = boxClosingHours.format(TimeBoxImpl.Formats.HH_mm).exec();
            throw new Error(`${shopData.shopNameAlias} have selected wrong closing hours of ${closingHours}`);
        }
    },
    parseWildcardDates(dateFilter, pickupTimeZone) {
        check.string.orFail(pickupTimeZone, 'pickupTimeZone');
        const TimeBoxImpl = getLuxonImpl();
        const date = dateFilter ? dateFilter : '*-*-*';
        const datesArray = isArray(date) ? date : [date];
        return concat([], ...datesArray.map((date) => {
            const [year, month, day] = date.split('-');
            const possibleYears = (() => {
                if (year === '*') {
                    const currentYear = TimeBoxImpl.timebox.now().convertTimezones(pickupTimeZone).year().exec();
                    return [currentYear, currentYear + 1, currentYear + 2].map((y) => y.toString());
                }
                else {
                    return year.split(',');
                }
            })();
            const possibleMonths = (() => {
                if (month === '*') {
                    return [...Array(12)].map((_, i) => {
                        return formatTime(i + 1);
                    });
                }
                else {
                    return month.split(',').map((month) => formatTime(month));
                }
            })();
            const getPossibleDays = (year, month) => {
                if (day === '*') {
                    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
                    return [...Array(daysInMonth)].map((_, i) => {
                        return formatTime(i + 1);
                    });
                }
                else {
                    return day.split(',').map((day) => formatTime(day));
                }
            };
            const monthsYearsCombo = possibleYears.reduce((carry, year) => {
                return [
                    ...carry,
                    ...possibleMonths.map((month) => {
                        return {
                            year,
                            month,
                        };
                    }),
                ];
            }, []);
            return monthsYearsCombo.reduce((carry, { year, month }) => {
                const possibleDays = getPossibleDays(year, month);
                return [...carry, ...possibleDays.map((day) => `${year}-${month}-${day}`)];
            }, []);
        }));
    },
    getUnavailableDates({ pickupDateTimeRules, pickupTimeZone, onlyIntersection = false }) {
        check.array.orFail(pickupDateTimeRules, 'pickupDateTimeRules');
        if (pickupDateTimeRules.length === 0) {
            return [];
        }
        const datePeriods = pickupDateTimeRules.map((productRule) => {
            return OrderHelperObject.parseWildcardDates(productRule.dateFilter, pickupTimeZone);
        });
        if (onlyIntersection) {
            if (flatten(datePeriods).length === 0) {
                return [];
            }
            const dateList = intersection(...datePeriods);
            return dateList.length === 0 ? [''] : dateList;
        }
        return uniq(datePeriods.reduce((carry, period) => {
            return [...carry, ...period];
        }, []));
    },
    getBTGUnavailableDates() {
        const db = `2025-12-30`;
        const de = `2025-12-31`;
        return getDates(db, de);
    },
    getOrderProductIdsAndQuantity(productSelections) {
        if (!productSelections) {
            return [];
        }
        const mainProducts = productSelections.map((ps) => {
            return {
                productId: ps.productId,
                quantity: ps.quantity,
            };
        });
        const groups = productSelections
            .filter((ps) => ps.groups)
            .map((ps) => {
            return ps.groups.map((group) => {
                return group.selectedProducts.map((sp) => {
                    if (typeof sp === 'string') {
                        return {
                            productId: sp,
                            quantity: ps.quantity,
                        };
                    }
                    else if (typeof sp === 'object') {
                        return OrderHelperObject.getOrderProductIdsAndQuantity([sp]);
                    }
                });
            });
        });
        return [...mainProducts, ...flattenDeep(groups)];
    },
    getOrderDepartmentIds(productSelections, productCatalog) {
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        if (!productSelections) {
            return [];
        }
        const productIds = OrderHelperObject.getOrderProductIdsAndQuantity(productSelections).map((pid) => pid.productId);
        const departmentIds = productIds
            .map((id) => productCatalog.getProduct(id, true).departmentId)
            .filter((id) => typeof id === 'number');
        return uniq(departmentIds);
    },
    isInTheDepartment(order, department) {
        check.object.orFail(order, 'order isInTheDepartment');
        check.object.orFail(department, 'department isInTheDepartment');
        return OrderHelperObject.isInTheDepartmentDeep(order.productSelections, department);
    },
    isInTheDepartmentDeep(productSelections, selectedDepartment) {
        if (!productSelections || !selectedDepartment) {
            return false;
        }
        const mainProducts = productSelections.some((ps) => selectedDepartment.productIds.some((pid) => pid === ps.productId));
        const groups = productSelections.some((selection) => {
            if (!selection.groups) {
                return false;
            }
            return selection.groups.some((group) => {
                return group.selectedProducts.some((selectedProduct) => {
                    if (typeof selectedProduct === 'string') {
                        return selectedDepartment.productIds.some((pid) => pid === selectedProduct);
                    }
                    else if (typeof selectedProduct === 'object') {
                        return OrderHelperObject.isInTheDepartmentDeep([selectedProduct], selectedDepartment);
                    }
                });
            });
        });
        return mainProducts || groups;
    },
    isProductInOrder(productSelections, productId) {
        if (!productSelections || !productId) {
            return false;
        }
        const mainProducts = productSelections.some((ps) => productId === ps.productId);
        const groups = productSelections.some((selection) => {
            if (!selection.groups) {
                return false;
            }
            return selection.groups.some((group) => {
                return group.selectedProducts.some((selectedProduct) => {
                    if (typeof selectedProduct === 'string') {
                        return productId === selectedProduct;
                    }
                    else if (typeof selectedProduct === 'object') {
                        return OrderHelperObject.isProductInOrder([selectedProduct], productId);
                    }
                });
            });
        });
        return mainProducts || groups;
    },
    assortmentRuleValidation({ assortmentRules, productSelections, shopId, tenantId, shouldAddProductIdsInsideGroups, }) {
        check.array.orFail(productSelections, 'productSelections');
        check.array.orFail(assortmentRules, 'assortmentRules');
        const productIds = OrderHelperObject.getProductIdsFromProductSelections(productSelections, shouldAddProductIdsInsideGroups);
        const rule = assortmentRules
            .filter((assortmentRule) => {
            return assortmentRule.shopId === shopId && assortmentRule.tenantId === tenantId;
        })
            .find((assortmentRule) => {
            return productIds.includes(assortmentRule.productId);
        });
        return rule ? rule : true;
    },
    getProductIds({ productSelections, includeDisabled, productCatalog }) {
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        return productSelections
            .map((product) => {
            return typeof product.productId !== 'undefined' ? product.productId : product.id;
        })
            .filter((productId) => {
            const catalogProduct = productCatalog.getProduct(productId, true);
            return includeDisabled || catalogProduct.disabled !== true;
        });
    },
    getDepartmentIds({ productSelections, includeDisabled, productCatalog }) {
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
        return uniq(productSelections
            .map((product) => {
            return typeof product.productId !== 'undefined' ? product.productId : product.id;
        })
            .map((productId) => {
            return productCatalog.getProduct(productId, true);
        })
            .filter((catalogProduct) => {
            return includeDisabled || catalogProduct.disabled !== true;
        })
            .map((catalogProduct) => {
            return catalogProduct.departmentId;
        }));
    },
    getFilteredRules({ rules, productIds, departmentIds, shopId, useShopOpeningHoursByWeekday = false }) {
        return rules.filter((rule) => {
            var _a, _b;
            if (useShopOpeningHoursByWeekday && !((_a = rule.productFilter) === null || _a === void 0 ? void 0 : _a.length) && !((_b = rule.departmentFilter) === null || _b === void 0 ? void 0 : _b.length)) {
                return false;
            }
            if (Array.isArray(rule.productFilter)) {
                const intersectionRes = intersection(productIds, rule.productFilter);
                if (intersectionRes.length === 0) {
                    return false;
                }
            }
            if (Array.isArray(rule.departmentFilter)) {
                const intersectionRes = intersection(departmentIds, rule.departmentFilter);
                if (intersectionRes.length === 0) {
                    return false;
                }
            }
            if (rule.shopFilter !== undefined) {
                if (shopId) {
                    if (Array.isArray(rule.shopFilter) && !rule.shopFilter.includes(shopId)) {
                        return false;
                    }
                }
                else {
                    if (Array.isArray(rule.shopFilter)) {
                        return false;
                    }
                }
            }
            return true;
        });
    },
    mapOrderLinesToOrderSummaryLines(orderLines) {
        if (!orderLines)
            return 0;
        return OrderHelperObject.mapToReceiptLines(orderLines).filter((line) => line.type !== 'Section');
    },
    printReceipt(receiptLines) {
        const getName = function (line) {
            return line.name;
        };
        let receipt = '';
        const N = 5, M = 50;
        const INDENT = indent(N);
        let total = 0;
        receiptLines.forEach((line) => {
            if (line.type === ReceiptLineType.Section) {
                receipt += INDENT + getName(line) + '\n';
            }
            else {
                if ('price' in line && 'totalPrice' in line && 'unitType' in line) {
                    total += line.totalPrice;
                    const msg = 'á ' + formatPrice(line.price) + ' pr. ' + line.unitType;
                    receipt +=
                        line.quantity +
                            indent(N - ('' + line.quantity).length) +
                            getName(line) +
                            '\n' +
                            INDENT +
                            msg +
                            indent(M - msg.length) +
                            formatPrice(line.totalPrice) +
                            '\n' +
                            (line.description && line.description[0]
                                ? line.description.map((d) => '\n' + INDENT + indent(d.depth * 2) + d.text).join('') + '\n\n'
                                : '');
                }
            }
        });
        receipt += indent(N) + 'i alt' + indent(M) + formatPrice(total) + '\n';
        return receipt;
    },
    mapToReceiptLines(topLevelOrderLines = [], i18n = {}, options = {}) {
        const generation = DomainCoreGeneration.detectObjectGeneration(topLevelOrderLines);
        if (generation === DomainCoreGeneration.Generation5) {
            const { messages, locale } = i18n || {};
            const localization = (() => {
                if (!locale || !messages) {
                    return null;
                }
                return { locale, messages };
            })();
            return mapToReceiptLineViewG5(mapToReceiptLinesG5(makeOrderLinesBackwardsCompatibleG5(topLevelOrderLines), options.showMenuAdditionsLine), localization, options);
        }
        else if (generation === DomainCoreGeneration.Generation4) {
            return this.mapToReceiptLinesG3();
        }
        else {
            return this.mapToReceiptLinesG1(topLevelOrderLines);
        }
    },
    mapToReceiptLinesG3() {
        throw new Error('Should never get here as G3 is phased out now.');
    },
    mapToReceiptLinesG1(topLevelOrderLines) {
        const receiptLines = [];
        const recurse = function (line, { groupStart, orderIndex, absoluteRootProductId, isParentAggregation }) {
            if (line.type === OrderLineType.Config || line.type === OrderLineType.Discount) {
                return;
            }
            let myGroupStart = groupStart;
            if ((line.type === OrderLineType.Aggregation &&
                (line.name === line.subLines[0].name || isParentAggregation !== true)) ||
                (line.type === OrderLineType.Product && line.includedInMainPrice === true)) {
            }
            else {
                const orderLine = (line.constructor !== OrderLine2 ? new OrderLine2(line) : line);
                const receiptLine = orderLine.getReceiptLine({
                    groupStart,
                    orderIndex,
                    absoluteRootProductId,
                });
                receiptLines.push(receiptLine);
                myGroupStart = false;
            }
            if (line.type !== OrderLineType.Aggregation ||
                (line.type === OrderLineType.Aggregation && isParentAggregation === true)) {
                (line.subLines || []).forEach((subline) => recurse(subline, {
                    groupStart: myGroupStart,
                    orderIndex,
                    absoluteRootProductId,
                    isParentAggregation: line.type === OrderLineType.Aggregation,
                }));
            }
        };
        if (topLevelOrderLines) {
            topLevelOrderLines.forEach((line, index) => {
                recurse(line, {
                    groupStart: true,
                    orderIndex: index,
                    absoluteRootProductId: line.rootProductId,
                    isParentAggregation: true,
                });
            });
        }
        return receiptLines;
    },
    _applyDiscountsForOrderLines(topLevelOrderLines) {
        const newOrderLines = [];
        const recurse = function (line, _newOrderLines) {
            const computedPrices = PriceMgr.compute(line);
            const newLine = Object.assign(Object.assign({}, line), computedPrices);
            newLine.subLines = [];
            if (line.subLines) {
                line.subLines.forEach((subLine) => recurse(subLine, newLine.subLines));
            }
            _newOrderLines.push(newLine);
        };
        topLevelOrderLines.forEach((line) => {
            recurse(line, newOrderLines);
        });
        return newOrderLines;
    },
    _accumulatePrices(topLevelOrderLines) {
        const accumulatePricesRecurse = function (line) {
            let _totalPriceAccum = 'totalPrice' in line && line.totalPrice ? line.totalPrice : 0;
            let _totalPriceBeforeAccum = 'totalPriceBefore' in line && line.totalPriceBefore ? line.totalPriceBefore : 0;
            let didAddFromSublines = false;
            if (line.subLines && line.subLines.length > 0) {
                line.subLines.forEach((subLine) => {
                    const { totalPriceAccum, totalPriceBeforeAccum } = accumulatePricesRecurse(subLine);
                    _totalPriceAccum += totalPriceAccum || 0;
                    _totalPriceBeforeAccum += Number.isFinite(totalPriceBeforeAccum)
                        ? totalPriceBeforeAccum
                        : Number.isFinite(totalPriceAccum)
                            ? totalPriceAccum
                            : 0;
                    didAddFromSublines = true;
                });
            }
            const result = {};
            if (_totalPriceAccum > 0) {
                result.totalPriceAccum = _totalPriceAccum;
            }
            if (didAddFromSublines) {
                if (_totalPriceBeforeAccum !== _totalPriceAccum) {
                    result.totalPriceBeforeAccum = _totalPriceBeforeAccum;
                }
            }
            else {
                if (_totalPriceBeforeAccum !== 0) {
                    result.totalPriceBeforeAccum = _totalPriceBeforeAccum;
                }
            }
            Object.assign(line, result);
            return result;
        };
        topLevelOrderLines.forEach(accumulatePricesRecurse);
        topLevelOrderLines.forEach((orderLine) => {
            if (orderLine.options && !orderLine.options.noMainProduct) {
                const hasDiscount = 'discount' in orderLine && !!orderLine.discount;
                const quantity = 'quantity' in orderLine ? orderLine.quantity : undefined;
                const additionToPriceAccum = orderLine.totalPriceBeforeAccum
                    ? orderLine.totalPriceBeforeAccum * (quantity - 1)
                    : 0;
                const additionToPriceBeforeAccum = orderLine.totalPriceBeforeAccum
                    ? orderLine.totalPriceBeforeAccum * quantity
                    : 0;
                if (!hasDiscount) {
                    orderLine.totalPriceAccum += additionToPriceAccum;
                    orderLine.totalPriceBeforeAccum = additionToPriceBeforeAccum;
                }
            }
        });
    },
    _fixPricesForProductsWithFreeAdditions(topLevelOrderLines) {
        topLevelOrderLines.forEach((orderLine) => {
            const _totalAccum = orderLine.totalPriceAccum;
            const _totalBeforeAccum = orderLine.totalPriceBeforeAccum;
            let hasFreeQuantity = false;
            if (orderLine.subLines && orderLine.subLines.length > 0) {
                orderLine.totalPriceAccum = 0;
                orderLine.totalPriceBeforeAccum = 0;
                orderLine.subLines.forEach((subLine) => {
                    if (subLine.options && subLine.options.freeQuantity) {
                        hasFreeQuantity = true;
                        subLine.totalPriceAccum = 0;
                        subLine.totalPriceBeforeAccum = 0;
                        const { totalCostForAdditions, sortedPricesWithQuantitiesForAdditions } = this.findTotalCostForAddition(subLine.subLines);
                        if (totalCostForAdditions > 0) {
                            let index = 0;
                            let numberOfFreeAdditions = subLine.options.freeQuantity.valueOf();
                            let totalDiscountForFreeAdditions = 0;
                            while (index <= sortedPricesWithQuantitiesForAdditions.length - 1) {
                                if (sortedPricesWithQuantitiesForAdditions[index] &&
                                    numberOfFreeAdditions <= sortedPricesWithQuantitiesForAdditions[index].quantity) {
                                    totalDiscountForFreeAdditions +=
                                        numberOfFreeAdditions * sortedPricesWithQuantitiesForAdditions[index].price;
                                    numberOfFreeAdditions = 0;
                                }
                                else if (sortedPricesWithQuantitiesForAdditions[index]) {
                                    totalDiscountForFreeAdditions +=
                                        sortedPricesWithQuantitiesForAdditions[index].quantity *
                                            sortedPricesWithQuantitiesForAdditions[index].price;
                                    numberOfFreeAdditions -= sortedPricesWithQuantitiesForAdditions[index].quantity;
                                }
                                index++;
                            }
                            orderLine.totalPriceAccum += (totalCostForAdditions - totalDiscountForFreeAdditions) * orderLine.quantity;
                            orderLine.totalPriceBeforeAccum += totalCostForAdditions * orderLine.quantity;
                            subLine.totalPriceAccum += totalCostForAdditions - totalDiscountForFreeAdditions;
                            subLine.totalPriceBeforeAccum += totalCostForAdditions;
                        }
                    }
                });
                if (hasFreeQuantity) {
                    orderLine.totalPriceAccum += orderLine.price * orderLine.quantity || 0;
                    orderLine.totalPriceBeforeAccum += orderLine.price * orderLine.quantity || 0;
                }
                else {
                    orderLine.totalPriceAccum = _totalAccum;
                    orderLine.totalPriceBeforeAccum = _totalBeforeAccum;
                }
            }
        });
    },
    findTotalCostForAddition(subLines) {
        const { totalCostForAdditions, pricesWithQuantitiesForAdditions } = subLines.reduce((carry, subLine) => {
            if (subLine.price && subLine.quantity) {
                carry.totalCostForAdditions += subLine.price * subLine.quantity;
                carry.pricesWithQuantitiesForAdditions.push({
                    price: subLine.price,
                    quantity: subLine.quantity,
                });
            }
            return carry;
        }, { totalCostForAdditions: 0, pricesWithQuantitiesForAdditions: [] });
        return {
            totalCostForAdditions,
            sortedPricesWithQuantitiesForAdditions: pricesWithQuantitiesForAdditions.sort((a, b) => b.price - a.price),
        };
    },
    getProductIdsFromProductSelections(productSelections, shouldAddProductIdsInsideGroups) {
        const flattenGroupProductIds = (groups) => {
            return groups.flatMap(({ selectedProducts }) => {
                return selectedProducts.flatMap((selectedProduct) => {
                    let productIds = [];
                    if (selectedProduct.productId) {
                        productIds.push(selectedProduct.productId);
                    }
                    if (selectedProduct.groups) {
                        productIds = productIds.concat(flattenGroupProductIds(selectedProduct.groups));
                    }
                    if (typeof selectedProduct === 'string') {
                        productIds.push(selectedProduct);
                    }
                    return productIds;
                });
            });
        };
        return productSelections.reduce((acc, curr) => {
            if (curr.productId) {
                acc.push(curr.productId);
            }
            if (curr.groups && shouldAddProductIdsInsideGroups) {
                acc = acc.concat(flattenGroupProductIds(curr.groups));
            }
            return acc;
        }, []);
    },
    getTotalQuantity(orderLines) {
        if (!orderLines)
            return 0;
        return orderLines.reduce((carry, item) => carry + OrderHelperObject.getTotalQuantityForOneOrderLine(item), 0);
    },
    getTotalQuantityForOneOrderLine(orderLine) {
        if (orderLine.type === OrderLineType.Config)
            return 0;
        return ((orderLine.quantity || 0) +
            (!orderLine.subLines
                ? 0
                : orderLine.subLines.reduce((carry, line) => carry + OrderHelperObject.getTotalQuantityForOneOrderLine(line), 0)));
    },
    calculateDiscountsForReceiptLines(receiptLines, vouchers = [], forViking = false) {
        const singleLineProductQuantity = {};
        const formattedReceiptLines = [];
        const isSingleSelectionProduct = (receiptLine) => {
            return receiptLine.options && receiptLine.options.canHaveMultipleProductSelections;
        };
        const calculateSimpleDiscountFixedAmount = (line) => {
            if (!isNaN(line.discount.discountValue) && typeof line.discount.discountValue === 'number') {
                const discountedPrice = line.price * 100 - line.discount.discountValue * 100;
                return discountedPrice >= 0 ? discountedPrice / 100 : line.price;
            }
            return line.price;
        };
        const isNumber = (number) => {
            return typeof number === 'number' && !isNaN(number);
        };
        const hasIntersection = (arr1, arr2) => {
            return arr1.filter((x) => arr2.includes(x)).length > 0;
        };
        const isEmptyArrayOrNull = (testArray) => {
            if (Array.isArray(testArray) && testArray.length > 0) {
                return false;
            }
            return true;
        };
        const canApplyDiscount = (receiptLine) => {
            if (!receiptLine.discount) {
                return false;
            }
            const discountApplicableVouchers = (() => {
                if (isEmptyArrayOrNull(receiptLine.discount.voucherPattern)) {
                    return true;
                }
                else if (!isEmptyArrayOrNull(receiptLine.discount.voucherPattern) &&
                    !isEmptyArrayOrNull(vouchers) &&
                    hasIntersection(receiptLine.discount.voucherPattern, vouchers)) {
                    return true;
                }
                return false;
            })();
            const discountApplicableGames = (() => {
                if (receiptLine.discount.options.giftDiscount === undefined ||
                    receiptLine.discount.options.giftDiscount === null) {
                    return true;
                }
                else if (receiptLine.discount.options.giftDiscount === true) {
                    if (receiptLine.addedByGame) {
                        return true;
                    }
                    return false;
                }
                return false;
            })();
            return discountApplicableVouchers && discountApplicableGames;
        };
        const noMainProductDiscount = {};
        for (const i in receiptLines) {
            const receiptLine = receiptLines[i];
            const { price } = receiptLine, trimmedLine = __rest(receiptLine, ["price"]);
            if (isSingleSelectionProduct(receiptLine)) {
                if (typeof singleLineProductQuantity[receiptLine.rootProductId] === 'number') {
                    singleLineProductQuantity[receiptLine.rootProductId] += 1;
                }
                else {
                    singleLineProductQuantity[receiptLine.rootProductId] = 1;
                }
            }
            if (canApplyDiscount(receiptLine)) {
                if (receiptLine.discount.discountType === 'simpleDiscountFixedAmount') {
                    if (receiptLine.type === ReceiptLineType.Section) {
                        noMainProductDiscount[receiptLine.absoluteRootProductId] = receiptLine.discount.discountValue;
                        formattedReceiptLines.push(receiptLine);
                    }
                    else if (receiptLine.type === ReceiptLineType.Product) {
                        const newPrice = calculateSimpleDiscountFixedAmount(receiptLine);
                        const oldPrice = receiptLine.price;
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, oldPrice: oldPrice, totalPrice: receiptLine.quantity * newPrice, oldTotalPrice: receiptLine.quantity * oldPrice }));
                    }
                }
                else if (receiptLine.discount.discountType === 'percentDiscount') {
                    if (receiptLine.type === ReceiptLineType.Product) {
                        if (isNumber(receiptLine.discount.discountValue)) {
                            const newPrice = (receiptLine.price * 100 - receiptLine.price * receiptLine.discount.discountValue) / 100;
                            const oldPrice = receiptLine.price;
                            formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, oldPrice: oldPrice, totalPrice: receiptLine.quantity * newPrice, oldTotalPrice: receiptLine.quantity * oldPrice }));
                        }
                        else {
                            formattedReceiptLines.push(Object.assign({}, receiptLine));
                        }
                    }
                    else {
                        formattedReceiptLines.push(Object.assign({}, receiptLine));
                    }
                }
                else if (receiptLine.discount.discountType === 'bulkDiscount' && !isSingleSelectionProduct(receiptLine)) {
                    const minQuantityToApply = parseFloat(receiptLine.discount.options.minQuantityToApply.toString());
                    const newUnitPrice = parseFloat(receiptLine.discount.options.newUnitPrice.toString());
                    if (isNumber(minQuantityToApply) && isNumber(newUnitPrice) && receiptLine.quantity >= minQuantityToApply) {
                        const newPrice = newUnitPrice;
                        const oldPrice = receiptLine.price;
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, oldPrice: oldPrice, totalPrice: receiptLine.quantity * newPrice, oldTotalPrice: receiptLine.quantity * oldPrice }));
                    }
                    else {
                        formattedReceiptLines.push(Object.assign({}, receiptLine));
                    }
                }
                else if (receiptLine.discount.discountType === 'twoForOne' && !isSingleSelectionProduct(receiptLine)) {
                    const newPrice = (receiptLine.totalPrice * 100) / 2 / 100;
                    const newPricePerUnit = (newPrice * 100) / receiptLine.quantity / 100;
                    formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPricePerUnit, totalPrice: newPrice }));
                }
                else if (receiptLine.discount.discountType === 'twoForOne' && isSingleSelectionProduct(receiptLine)) {
                    if (forViking) {
                        const newPrice = (price * 100) / 2 / 100;
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, totalPrice: newPrice }));
                    }
                    else {
                        if (singleLineProductQuantity[receiptLine.rootProductId] === 2) {
                            formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: 0, totalPrice: 0 }));
                            singleLineProductQuantity[receiptLine.rootProductId] = 0;
                        }
                        else {
                            formattedReceiptLines.push(receiptLine);
                        }
                    }
                }
            }
            else {
                if (receiptLine.type === ReceiptLineType.Product) {
                    if (noMainProductDiscount[receiptLine.absoluteRootProductId] > 0) {
                        if (receiptLine.price >= noMainProductDiscount[receiptLine.absoluteRootProductId]) {
                            const newPrice = price - noMainProductDiscount[receiptLine.absoluteRootProductId];
                            formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, totalPrice: newPrice }));
                            noMainProductDiscount[receiptLine.absoluteRootProductId] = 0;
                        }
                        else {
                            noMainProductDiscount[receiptLine.absoluteRootProductId] -= price;
                            formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: 0, totalPrice: 0 }));
                        }
                    }
                    else {
                        formattedReceiptLines.push(receiptLine);
                    }
                }
                else {
                    formattedReceiptLines.push(receiptLine);
                }
            }
        }
        return formattedReceiptLines;
    },
    shortOrderNumber(orderNumber) {
        return orderNumber.toString().substr(-4, 4);
    },
    formatPickupDate(pickupDate) {
        const dateStr = pickupDate.toString().split('-');
        return dateStr[2] + '-' + dateStr[1] + '-' + dateStr[0];
    },
    formatPickupTime(pickupTime) {
        const dateStr = pickupTime.toString().split(':');
        return dateStr[0] + ':' + dateStr[1];
    },
    getVerbosePickupDateTime(order) {
        const time = order.pickupTime.toString();
        return OrderHelperObject.formatPickupDate(order.pickupDate) + ' kl. ' + time.split(':').slice(0, 2).join(':');
    },
    capitalize(str) {
        return str[0].toUpperCase() + str.slice(1);
    },
    parseLocaleDate(localDate, pickupTime) {
        const time = pickupTime.toString();
        const newLocalDate = localDate.split(' ').filter((el) => el !== 'd.');
        newLocalDate[0] = this.capitalize(newLocalDate[0]);
        return newLocalDate.slice(0, 4).join(' ') + ' kl. ' + time.split(':').slice(0, 2).join(':');
    },
    getLocaleDate({ pickupDate, pickupTime }, localeString, dateFormat = 'DDDD', type = '') {
        const localesMap = {
            da: 'da',
            fo: 'fo',
            no: 'no',
            de: 'de',
            en: 'en-GB',
        };
        const formatsMap = {
            da: 'd. MMMM yyyy',
            fo: 'd MMMM yyyy',
            de: 'd. MMMM yyyy',
            gl: 'd. MMMM yyyy',
        };
        const newDate = DateTime.fromFormat(pickupDate.toString(), 'yyyy-MM-dd', {
            locale: localesMap[localeString],
        });
        switch (type) {
            case 'dateOnly':
                return newDate.toFormat(formatsMap[localeString] || dateFormat);
            case 'timeOnly':
                return pickupTime;
            default:
                return this.parseLocaleDate(newDate.toFormat(formatsMap[localeString] || dateFormat), pickupTime);
        }
    },
    getTotalProductSelectionsQuantity(productSelections) {
        return productSelections.reduce((carry, selection) => carry +
            (selection.quantity || 0) +
            (selection.groups || []).reduce((sum, g) => sum + (g.selectedProducts || []).reduce((s, p) => s + (p ? p.quantity || 0 : 0), 0), 0), 0);
    },
    getForeignOrderNumberBasedOnFt(displayNewForeignOrderNumber, foreignOrderNumber, shortForeignOrderNumber) {
        return displayNewForeignOrderNumber ? shortForeignOrderNumber : foreignOrderNumber;
    },
    getForeignOrderNumber({ order, tenant, getAllIds, }) {
        var _a, _b, _c;
        const ftValue = (_a = tenant.featureToggle.displayNewForeignOrderNumber) === null || _a === void 0 ? void 0 : _a.value;
        const fon = order.foreignOrderNumber;
        const shortFon = ((_c = (_b = order.orderOptions) === null || _b === void 0 ? void 0 : _b.newForeignOrderNumber) === null || _c === void 0 ? void 0 : _c.toString()) || null;
        if (getAllIds === 'true') {
            return {
                shortForeignOrderNumber: (fon ? shortFon : null),
                foreignOrderNumber: fon,
                kioskReceiptForeignOrderNumber: (ftValue && fon ? shortFon : fon),
            };
        }
        else {
            return (ftValue && fon ? shortFon : fon);
        }
    },
    getOrderDetails({ order }) {
        const { orderNumber, orderLines, receiptLines, totalAmountCents, shopName, shopAddress, shopId, pickupDate, pickupTime, pickupTimeZone, orderOptions: { deliveryChoice }, customerDetails: { name: customerName }, } = order;
        return {
            orderNumber,
            orderLines,
            receiptLines,
            totalAmountCents,
            shopName,
            shopAddress,
            shopId,
            pickupDate,
            pickupTime,
            pickupTimeZone,
            deliveryChoice,
            customerName,
        };
    },
    getOrderItemKey(index, orderLine) {
        if (orderLine.type === OrderLineType.CombinatorLine) {
            return `${index}_${simpleHashCode(orderLine.productIds.join())}`;
        }
        else {
            return 'productId' in orderLine ? `${index}_${orderLine.productId}` : undefined;
        }
    },
    getProductCatalogDataForEditingOrder(productCatalogData, productSelections) {
        const flattenProductSelections = productSelections.reduce((acc, cur) => {
            return [...acc, ...extractProductsFromProductSelection(cur)];
        }, []);
        return Object.assign(Object.assign({}, productCatalogData), { products: productCatalogData.products.map((product) => {
                const orderProduct = flattenProductSelections.find((ps) => ps.productId === product.id);
                if (!orderProduct) {
                    return product;
                }
                const groups = (() => {
                    return product.groups.map((gr) => {
                        var _a;
                        const orderProductGroup = (_a = orderProduct.groups) === null || _a === void 0 ? void 0 : _a.find((el) => el.groupId === gr.id);
                        if (!(orderProductGroup === null || orderProductGroup === void 0 ? void 0 : orderProductGroup.options)) {
                            return gr;
                        }
                        return Object.assign(Object.assign({}, gr), { options: Object.assign(Object.assign({}, gr.options), { groupPrice: orderProductGroup.options.groupPrice }) });
                    });
                })();
                return Object.assign(Object.assign({}, product), { groups, price: orderProduct.price ? orderProduct.price : product.price, priceStructure: orderProduct.priceStructure ? Object.assign({}, orderProduct.priceStructure) : product.priceStructure });
            }) });
    },
    getTotalPrice(lines, productCatalog, additionalArgs) {
        return getTotalPrice(lines, productCatalog, additionalArgs);
    },
    getTotalGlobalDiscount(lines, productCatalog, additionalArgs) {
        return getTotalGlobalDiscount(lines, productCatalog, additionalArgs);
    },
    getTotalPriceCents(lines, productCatalog, additionalArgs) {
        return getTotalPriceCents(lines, productCatalog, additionalArgs);
    },
    getTotalPriceWithoutDiscount(orderLines, includeReturnableItems) {
        return getTotalPriceWithoutDiscount(orderLines, includeReturnableItems);
    },
    getTotalPriceWithDiscount(orderLines, productCatalog, additionalArgs) {
        return getTotalPriceWithDiscount(orderLines, productCatalog, additionalArgs);
    },
};
function simpleHashCode(str) {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        const chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
    }
    return hash;
}
export default OrderHelperObject;
